import { Theme } from '@eversports/react-components/design-tokens/theme'
import Collapse from '@eversports/react-components/primitives/Collapse'
import Divider from '@eversports/react-components/primitives/Divider'
import Flex from '@eversports/react-components/primitives/Flex'
import Heading from '@eversports/react-components/primitives/Heading'
import Icon, { Icons } from '@eversports/react-components/primitives/Icon'
import MonetaryValue from '@eversports/react-components/primitives/MonetaryValue'
import Processing from '@eversports/react-components/primitives/Processing'
import Text from '@eversports/react-components/primitives/Text'
import { makeStyles } from '@mui/styles'
import { useState } from 'react'

import useCheckoutContext from '../../data/useCheckoutContext'
import useSummary from '../../data/useSummary'
import { Localized } from '../../localization/react'
import { MobileBookingDetails } from '../BookingDetails'
import BottomFixedCollapsibleSection from '../BottomFixedCollapsibleSection'
import { VoucherSelection } from '../VoucherSelection'
import { VoucherSelectionState } from '../VoucherSelection/VoucherSelection'
import { PriceTableItemMembership } from '../../typings/PriceTable'
import MembershipPromotionInfo from '../Product/MembershipPromotionInfo'
import CorporateBenefitsToggle from './CorporateBenefitsToggle'
import { SpotBookingInformation } from './SpotBookingInformation'

import SummaryButton from './SummaryButton'
import SummaryItems from './SummaryItems'

const useStyles = makeStyles((theme: Theme) => ({
  uppercase: {
    textTransform: 'uppercase',
  },
  voucherContainer: {
    paddingBottom: theme.layoutSpacing.dense,
  },
}))

const MobileSummary = () => {
  const classes = useStyles()
  const context = useCheckoutContext()
  const errors = context.getErrors()
  const [isVoucherExpanded, setIsVoucherExpanded] = useState(false)
  const { isLoading, items, total, isSettleWithAConfirmWithOption, showVoucher, hasSpotBooking } = useSummary()
  const [hasFetchedData, setHasFetchedData] = useState(false)

  // This is used to make sure the component doesn't get back into a state
  // where a product is not selected after the first selection is done
  // thus avoiding the visual flicker that would happen
  if (items && items.length > 0 && !hasFetchedData) {
    setHasFetchedData(true)
  }

  const membershipItem = items?.find(
    (item) => item.__typename === 'PriceTableItemMembership',
  ) as PriceTableItemMembership

  return (
    <BottomFixedCollapsibleSection
      collapsibleContent={
        <>
          <MobileBookingDetails />
          <Divider />
          <SummaryItems items={items} isSettleWithAConfirmWithOption={isSettleWithAConfirmWithOption} />
        </>
      }
      showExpandIcon={hasFetchedData}
    >
      <>
        {membershipItem && membershipItem.promotion && membershipItem.paymentInterval && (
          <Flex marginTop="default" marginBottom="default">
            <MembershipPromotionInfo
              promotion={membershipItem.promotion}
              recurringPrice={membershipItem.recurringPrice}
            />
          </Flex>
        )}
        {!isSettleWithAConfirmWithOption && <CorporateBenefitsToggle />}
        {showVoucher && (
          <>
            <Flex
              paddingBottom="dense"
              paddingTop="dense"
              onClick={() => setIsVoucherExpanded(!isVoucherExpanded)}
              fullWidth
            >
              <Text size="small">
                {!isVoucherExpanded ? (
                  <Localized id="show-voucher-redemption" />
                ) : (
                  <Localized id="hide-voucher-redemption" />
                )}
              </Text>
            </Flex>
            <Collapse in={isVoucherExpanded}>
              <div className={classes.voucherContainer}>
                <VoucherSelection initialState={VoucherSelectionState.ACTIVE} />
              </div>
            </Collapse>
          </>
        )}
        {hasSpotBooking && (
          <Flex paddingBottom="dense" paddingTop="dense" fullWidth>
            <SpotBookingInformation />
          </Flex>
        )}
        <Divider marginBottom="default" />
        <Flex justifyContent="space-between" alignItems="center">
          <Flex flexDirection="column" alignItems="flex-start">
            {hasFetchedData ? (
              !isSettleWithAConfirmWithOption &&
              total && (
                <>
                  <Flex horizontalSpacing="dense" alignItems="center">
                    <span className={classes.uppercase}>
                      <Text fontWeight="bold" size="small">
                        <Localized id="summary-total" />
                      </Text>
                    </span>
                    <Text size="small">
                      <Localized id="summary-total-vat" />
                    </Text>
                  </Flex>
                  <Processing isLoading={isLoading}>
                    <Heading is="h6" variant="normal" color="primary.main" data-testid="total-checkout-value">
                      <MonetaryValue value={total.value} currencyCode={total.currency} />
                    </Heading>
                  </Processing>
                </>
              )
            ) : (
              <Heading is="h6" variant="small">
                <Localized id="summary-choose-product" />
              </Heading>
            )}
          </Flex>
          <SummaryButton
            fullWidth={items && items.length > 0 && isSettleWithAConfirmWithOption}
            isSettleWithAConfirmWithOption={isSettleWithAConfirmWithOption}
          />
        </Flex>
        {errors &&
          errors.map((error) => (
            <Flex horizontalSpacing="dense" marginTop="dense" key={error}>
              <Icon icon={Icons.WARNING} color="danger" size="large" />
              <Text size="small" color="danger">
                {error}
              </Text>
            </Flex>
          ))}
      </>
    </BottomFixedCollapsibleSection>
  )
}

export default MobileSummary
